import { AxiosInstance } from "axios"
import {
    AcquiringReport,
    BalanceAccount,
    BalancePaymentDetails,
    DateType,
    FeeItem,
    PaymentForPeriod,
    TransferItem,
} from "./supplierAcquiringReportsTypes"

export enum SupplierAcquiringReportsQuery {
    FEES_LIST = "acquiring-fees-for-supplier",
    BANK_TRANSFERS_LIST = "acquiring-bank-transfers-for-supplier",
    BALANCE_ACCOUNTS = "balance-accounts-for-supplier",
    BALANCE_LIST = "balance-for-supplier",
    BALANCE_DETAILS_LIST = "balance-details-for-supplier",
    PAYMENTS_FOR_PERIOD = "payments-for-period",
}

type SupplierAcquiringReportsParams = Readonly<{
    supplierId: number
    fromDate: string
    toDate: string
    dateType: DateType
}>

export enum ReportState {
    WARNING = "WARNING",
}

export type ReportStatus = Readonly<{
    state: ReportState
    message?: string
}>

export type ReportDetails = Readonly<{
    startDate: string
    endDate: string
    reportTime: string
    status: ReportStatus
}>

export type SupplierAcquiringFeesResponse = Readonly<{
    reportDetails?: ReportDetails
    fees: FeeItem[]
}>

export async function fetchSupplierAcquiringFees(
    { supplierId, ...rest }: SupplierAcquiringReportsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierAcquiringFeesResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/accounting/acquiring/adyen/fees`, {
        params: { ...rest },
        signal,
    })

    return data
}

type SupplierAcquiringBankTransfersParams = Readonly<{
    supplierId: number
    includePrevBankTransfer?: boolean
    fromDate: string
    toDate: string
}>

export type SupplierAcquiringBankTransfersResponse = Readonly<{
    transfers: TransferItem[]
}>

export async function fetchSupplierAcquiringBankTransfers(
    { supplierId, fromDate, toDate, includePrevBankTransfer = false }: SupplierAcquiringBankTransfersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierAcquiringBankTransfersResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/accounting/acquiring/adyen/bankTransfers`, {
        params: {
            fromDate,
            toDate,
            includePrevBankTransfer,
        },
        signal,
    })

    return data
}

type FetchBalanceAccountsParams = Readonly<{
    supplierId: number
}>

export type FetchBalanceAccountsResponse = Readonly<{
    balanceAccounts: BalanceAccount[]
}>

export async function fetchBalanceAccountsResult(
    { supplierId }: FetchBalanceAccountsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchBalanceAccountsResponse> {
    const url = `/suppliers/${supplierId}/acquirers/balanceAccounts`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

// Balance
type SupplierBalanceParams = Readonly<{
    supplierId: number
    fromDate: string
    toDate: string
    merchantId?: string
}>

export type SupplierBalanceResponse = Readonly<{
    acquiringReports: AcquiringReport[]
}>

export async function fetchSupplierBalance(
    { supplierId, fromDate, toDate, merchantId }: SupplierBalanceParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierBalanceResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/accounting/acquiring/adyen/acquiringReport`, {
        params: {
            fromDate,
            toDate,
            merchantId,
        },
        signal,
    })

    return data
}

type SupplierBalanceDetailsParams = Readonly<{
    supplierId: number
    fromDateTime: string
    toDateTime: string
}>

export type SupplierBalanceDetailsResponse = Readonly<{
    payments: BalancePaymentDetails[]
}>

export async function fetchSupplierBalanceDetails(
    { supplierId, fromDateTime, toDateTime }: SupplierBalanceDetailsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SupplierBalanceDetailsResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/accounting/acquiring/adyen/payments`, {
        params: {
            fromDateTime,
            toDateTime,
        },
        signal,
    })

    return data
}

type FetchPaymentsForPeriodParams = Readonly<{
    supplierId: number
    fromDateTime: string | null
    toDateTime: string
}>

export type FetchPaymentsForPeriodResponse = Readonly<{
    payments: Array<PaymentForPeriod>
}>

export async function fetchPaymentsForPeriod(
    { supplierId, fromDateTime, toDateTime }: FetchPaymentsForPeriodParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchPaymentsForPeriodResponse> {
    const { data } = await axios.get(`/suppliers/${supplierId}/accounting/acquiring/adyen/payments`, {
        params: {
            fromDateTime,
            toDateTime,
        },
        signal,
    })

    return data
}
